import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useStudentMe } from '../modules/roster/hooks/useStudentMe';
import { getTeacherProfile } from '../modules/roster/api/teacher.client';
import { USER_ROLES } from '../utils/constants';
import { resolveAvatarUrl } from '../utils/avatarUrl';
import { getUserDisplayName, getUserInitials, resolveAvatarNameParts } from '../utils/userInitials';

const ROSTER_TEACHER_ME_QUERY_KEY = ['roster', 'teacher', 'me'] as const;

export function useUserAvatarInfo() {
  const { user } = useAuth();
  const { student, enabled: studentRosterEnabled, isLoading: studentRosterLoading } = useStudentMe();

  const isTeacher = Boolean(user?.roles?.includes(USER_ROLES.TEACHER));
  const isStudent = Boolean(user?.roles?.includes(USER_ROLES.STUDENT));
  const teacherQuery = useQuery({
    queryKey: ROSTER_TEACHER_ME_QUERY_KEY,
    queryFn: getTeacherProfile,
    enabled: isTeacher,
    staleTime: 5 * 60 * 1000,
  });

  const avatarUrl = useMemo(
    () => resolveAvatarUrl(user?.profile?.avatar_url),
    [user?.profile?.avatar_url]
  );

  const nameParts = useMemo(() => {
    if (!user) return null;

    const waitingForStudentRoster = isStudent && studentRosterEnabled && studentRosterLoading && !student;
    const waitingForTeacherRoster = isTeacher && teacherQuery.isLoading && !teacherQuery.data;
    if (waitingForStudentRoster || waitingForTeacherRoster) {
      return null;
    }

    return resolveAvatarNameParts({
      profileName: user.profile?.name,
      roles: user.roles,
      student,
      teacher: teacherQuery.data,
    });
  }, [
    user,
    student,
    isStudent,
    isTeacher,
    studentRosterEnabled,
    studentRosterLoading,
    teacherQuery.data,
    teacherQuery.isLoading,
  ]);

  const initials = useMemo(() => (nameParts ? getUserInitials(nameParts) : null), [nameParts]);

  const displayName = useMemo(
    () => (nameParts ? getUserDisplayName(nameParts) : null),
    [nameParts]
  );

  return {
    avatarUrl,
    initials,
    displayName,
    isLoading:
      (isStudent && studentRosterEnabled && studentRosterLoading) ||
      (isTeacher && teacherQuery.isLoading),
  };
}
